import { ColorCodes } from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ScreenSpaceEventType } from 'cesium';
import { isNil } from 'lodash';
import {
  selectMap3DState,
  selectMap3dWorkspace,
  setAIMode,
} from 'map-3d/shared/map-3d-slices';
import { useEffect, useRef, useState } from 'react';
import { FileType } from 'shared/api';
import {
  type CesiumMouseLocationCoordinates,
  getCartographicPositionFromMousePosition,
} from 'shared/cesium';
import { ErrorSlugs, KeyboardKey } from 'shared/enums';
import { isCoordinateWithinBounds } from 'shared/helpers';
import { RoutesEnum } from 'shared/routes';
import { useAppWebSocket } from '../../web-socket/use-app-websocket';
import { buildWebSocketUrl } from '../../web-socket/web-socket-url';
import { setAIAuthCookie } from '../ai-auth';
import { ADD_AREA_CURSOR, REMOVE_AREA_CURSOR } from '../ai-cursors';
import { PolygonAIPromptRenderer } from '../ai-prompt-renderer';
import { PolygonAISessionController } from '../ai-session-controller';
import { PolygonAIActionType, PromptType } from '../enums';
import {
  type PolygonAIErrorPayload,
  type PolygonAIResponse,
  type PolygonAISession,
  type PolygonAISessionInitMessage,
} from '../types';

// Constants.
const HOVER_STYLE = {
  polygonStyleOptions: {
    fillColor: ColorCodes.Primary500,
    fillOpacity: 0.6,
    strokeColor: ColorCodes.Primary700,
    strokeThickness: 2,
    strokeDashed: true,
    strokeDashLength: 8,
  },
};

const SELECT_STYLE = {
  polygonStyleOptions: {
    fillColor: ColorCodes.Primary500,
    fillOpacity: 0.6,
    strokeColor: ColorCodes.Primary700,
    strokeThickness: 2,
    strokeDashed: false,
  },
};

// Type.
type IncomingMessage =
  | PolygonAISessionInitMessage
  | PolygonAIResponse
  | PolygonAIErrorPayload;

const isErrorPayload = (data: IncomingMessage): data is PolygonAIErrorPayload =>
  'type' in data && data.type === 'error';

const isSessionInitMessage = (
  data: IncomingMessage,
): data is PolygonAISessionInitMessage => 'session_id' in data;

const isDatasetReadyResponse = (
  data: IncomingMessage,
): data is PolygonAIResponse =>
  'is_success' in data &&
  data.action_type === PolygonAIActionType.SetDataset &&
  data.is_success === true;

export const usePolygonAISession = (enabled: boolean): PolygonAISession => {
  // Selectors.
  const { cesiumProxy } = useAppSelector(selectMap3DState);
  const {
    drawingTools,
    polygonAiConfig: polygonAi,
    workspaceLayers,
  } = useAppSelector(selectMap3dWorkspace);
  const { isAiModeEnabled, promptType, activeOrthoLayerId } = polygonAi;

  // States.
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [confirmedPolygons, setConfirmedPolygons] = useState<string[]>([]);
  const [hasPrompts, setHasPrompts] = useState(false);

  // Refs.
  const controllerRef = useRef<PolygonAISessionController | null>(null);
  const rendererRef = useRef<PolygonAIPromptRenderer | null>(null);
  const promptTypeRef = useRef<PromptType>(promptType);
  const isDatasetReadyRef = useRef(false);

  // Derived values.
  const isSessionActive = enabled && isAiModeEnabled;
  const polygonTool = drawingTools?.polygonTool;
  const layer = activeOrthoLayerId
    ? workspaceLayers[activeOrthoLayerId]
    : undefined;
  const cogFile = layer?.files?.find(
    (file) => file?.type === FileType.OrthomosaicCog,
  );
  const orthomosaicS3Uri = cogFile?.s3Key ? `s3://${cogFile.s3Key}` : '';
  const orthoBounds = cogFile?.properties?.bounds;
  const url = enabled ? buildWebSocketUrl(RoutesEnum.Segmentation) : null;

  // Helpers.
  const isWithinOrthoBounds = (longitude: number, latitude: number): boolean =>
    isNil(orthoBounds) ||
    isCoordinateWithinBounds(longitude, latitude, orthoBounds);

  // Dispatch.
  const dispatch = useAppDispatch();

  // WebSocket.
  const { sendJsonMessage } = useAppWebSocket<IncomingMessage>({
    url,
    enabled: isSessionActive,

    onMessage: (data: IncomingMessage) => {
      if (isSessionInitMessage(data)) {
        setSessionId(data.session_id);
        return;
      }

      if (isDatasetReadyResponse(data)) {
        isDatasetReadyRef.current = true;
        return;
      }

      if (isErrorPayload(data)) {
        if (data.slug === ErrorSlugs.PromptOutsideDataset) {
          return;
        }

        console.error(data.message);
        return;
      }

      controllerRef.current?.handleResponse(data);
    },

    onError: () => {
      setSessionId(null);
      console.error('Polygon AI WebSocket connection error');
      dispatch(setAIMode(false));
      polygonTool?.activate();
    },

    onClose: () => {
      setSessionId(null);
      console.error('Polygon AI WebSocket connection closed');
      dispatch(setAIMode(false));
      isDatasetReadyRef.current = false;
    },
  });

  // Effects.
  useEffect(() => {
    if (isSessionActive) {
      setAIAuthCookie();
    }
  }, [isSessionActive]);

  useEffect(() => {
    if (!isSessionActive || isNil(sessionId) || !orthomosaicS3Uri) {
      return;
    }

    isDatasetReadyRef.current = false;
    clearPredictions();
    controllerRef.current?.reset();

    sendJsonMessage({
      session_id: sessionId,
      action_type: PolygonAIActionType.SetDataset,
      orthomosaic_cog_uri: orthomosaicS3Uri,
    });
  }, [isSessionActive, sessionId, orthomosaicS3Uri]);

  useEffect(() => {
    promptTypeRef.current = promptType;

    if (promptType === PromptType.Negative) {
      polygonTool?.deletePolygonsByProperty('isSAMHoverPrediction', true);
    }
  }, [promptType, polygonTool]);

  useEffect(() => {
    if (!isSessionActive) {
      destroySession();
    }

    return () => {
      destroySession();
    };
  }, [isSessionActive]);

  useEffect(() => {
    if (!isSessionActive || isNil(sessionId)) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === KeyboardKey.Z) {
        event.preventDefault();

        undo();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isSessionActive, sessionId]);

  useEffect(() => {
    if (
      !isSessionActive ||
      isNil(sessionId) ||
      isNil(cesiumProxy) ||
      isNil(polygonTool)
    ) {
      return;
    }

    const viewer = cesiumProxy.viewer;

    const handleAIMouseMove = ({
      longitude,
      latitude,
    }: CesiumMouseLocationCoordinates) => {
      if (isNil(controllerRef.current)) {
        return;
      }

      const isWithinBounds = isWithinOrthoBounds(longitude, latitude);

      if (!isNil(orthoBounds)) {
        const promptCursor =
          promptTypeRef.current === PromptType.Positive
            ? ADD_AREA_CURSOR
            : REMOVE_AREA_CURSOR;

        viewer.canvas.style.cursor = isWithinBounds ? promptCursor : 'default';
      }

      if (!isWithinBounds) {
        return;
      }

      if (
        isDatasetReadyRef.current &&
        promptTypeRef.current === PromptType.Positive
      ) {
        controllerRef.current.hover(`POINT(${longitude} ${latitude})`);
      }
    };

    const controller = new PolygonAISessionController(
      sessionId,
      sendJsonMessage,
      clearPredictions,
      renderPredictions,
    );

    controllerRef.current = controller;

    const renderer = new PolygonAIPromptRenderer(viewer, setHasPrompts);

    rendererRef.current = renderer;

    cesiumProxy.addMouseLocationListener(handleAIMouseMove);

    viewer.screenSpaceEventHandler.setInputAction((click: { position }) => {
      if (!isDatasetReadyRef.current) {
        return;
      }

      const position = getCartographicPositionFromMousePosition(
        click.position,
        viewer,
      );

      if (isNil(position)) {
        return;
      }

      const { longitude, latitude, altitude } = position;

      if (!isWithinOrthoBounds(longitude, latitude)) {
        return;
      }

      controller.select(
        `POINT(${longitude} ${latitude})`,
        promptTypeRef.current,
      );

      renderer.addPrompt(longitude, latitude, altitude, promptTypeRef.current);
    }, ScreenSpaceEventType.LEFT_CLICK);

    return () => {
      cesiumProxy.removeMouseLocationListener(handleAIMouseMove);

      viewer.screenSpaceEventHandler.removeInputAction(
        ScreenSpaceEventType.LEFT_CLICK,
      );
    };
  }, [
    sessionId,
    isSessionActive,
    sendJsonMessage,
    cesiumProxy,
    orthomosaicS3Uri,
    orthoBounds,
    polygonTool,
  ]);

  // Handlers.
  const renderPredictions = (wktPolygons: string[], isHover: boolean) => {
    if (isNil(polygonTool)) {
      return;
    }

    polygonTool.deletePolygonsByProperty(
      isHover ? 'isSAMHoverPrediction' : 'isSAMPrediction',
      true,
    );

    const properties = isHover
      ? { isSAMPrediction: true, isSAMHoverPrediction: true }
      : { isSAMPrediction: true };
    const styleOptions = isHover ? HOVER_STYLE : SELECT_STYLE;

    const polygonId = `sam-${crypto.randomUUID()}`;

    wktPolygons.forEach((wkt) => {
      polygonTool.importWKT(wkt, polygonId, properties, '', styleOptions);
    });

    if (!isHover) {
      setConfirmedPolygons(wktPolygons);
    }
  };

  const clearPredictions = () => {
    polygonTool?.deletePolygonsByProperty('isSAMPrediction', true);
    polygonTool?.deletePolygonsByProperty('isSAMHoverPrediction', true);
    rendererRef.current?.clearPrompts();
    setConfirmedPolygons([]);
  };

  const destroySession = () => {
    controllerRef.current?.destroy();
    rendererRef.current?.destroy();

    controllerRef.current = null;
    rendererRef.current = null;

    if (cesiumProxy) {
      cesiumProxy.viewer.canvas.style.cursor = 'default';
    }

    polygonTool?.deletePolygonsByProperty('isSAMPrediction', true);
    setConfirmedPolygons([]);
    isDatasetReadyRef.current = false;
    setSessionId(null);
  };

  const undo = () => {
    controllerRef.current?.undo();
    rendererRef.current?.removeLastPrompt();

    if (!hasPrompts) {
      controllerRef.current?.exitSelectMode();
    }
  };

  const reset = () => {
    controllerRef.current?.reset();
  };

  const getConfirmedPolygons = () => confirmedPolygons;

  const hasSelectedPrompt = () =>
    controllerRef.current?.hasSelectedPrompt() ?? false;

  const clearAfterSave = () => {
    controllerRef.current?.reset();
    clearPredictions();
  };

  return {
    undo,
    reset,
    hasPrompts,
    getConfirmedPolygons,
    clearAfterSave,
    hasSelectedPrompt,
  };
};
